import { Button, Input, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import { useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetBookings } from "~/services/booking";
import { TBooking } from "~/types";

interface BookingListProps {
  selectedBookingId: string | null;
  onSelectBooking: (id: string | null) => void;
}
export const BookingList = ({
  selectedBookingId,
  onSelectBooking,
}: BookingListProps) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [search] = useDebounceValue(searchTerm, 500);
  const { data: bookings, isLoading } = useGetBookings({ search });

  const columns: ColumnsType<TBooking> = [
    {
      title: "Booking ID",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: "25%",
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: "Client",
      key: "client",
      width: "25%",
      render: (_, record) => (
        <span>
          {record.client?.companyName ||
            [record.client?.firstName, record.client?.lastName]
              .filter(Boolean)
              .join(" ")
              .trim() ||
            "Unnamed Client"}
        </span>
      ),
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
      key: "project",
      width: "25%",
    },
    {
      title: "Date",
      dataIndex: ["bookingDate"],
      key: "date",
      width: "15%",
      render: (date) => (
        <span>{(date ? dayjs(date) : dayjs()).format("YYYY-MM-DD")}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Button
          type="text"
          onClick={() =>
            onSelectBooking(selectedBookingId === record.id ? null : record.id)
          }
        >
          {selectedBookingId === record.id ? "Deselect" : "Select"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search Bookings
        </label>
        <div className="relative">
          <Input
            placeholder="Search by booking ID, client name, or project..."
            prefix={<Search className="text-gray-400" size={20} />}
            className="w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-6">
        <Table
          loading={isLoading}
          size="small"
          columns={columns}
          dataSource={bookings?.items}
          rowKey="id"
          pagination={{
            total: bookings?.meta.itemCount,
            pageSize: 10,
            current: page,
            onChange: (page) => setPage(page),
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          rowClassName={(record) =>
            selectedBookingId === record.id ? "bg-blue-50" : "hover:bg-gray-50"
          }
          className="border rounded-lg"
        />
      </div>
    </>
  );
};
